import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Upload,
  Download,
  CheckCircle,
  XCircle,
  Building2,
  BriefcaseBusiness,
  UserRound,
  Phone,
  Mail,
  UsersRound,
  Shapes,
  Share2,
  MapPin,
  Plus,
} from "lucide-react";
import { Lead } from "../../types/crm";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import type { UploadRequestOption } from "rc-upload/lib/interface";
import type { UploadFile, UploadProps } from "antd/es/upload";

interface AddLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddLead: (lead: Omit<Lead, "id" | "createdAt">) => void;
  rmList: { id: number; name: string }[];
  onLeadAdded: () => void;
  onUploadSuccess: () => void;
}

interface CsvUploadResult {
  successful: any[];
  failed: any[];
  total: number;
}

interface DataType {
  key: string;
  companyName: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  rmName: string;
  reason?: string;
  leadType?: string;
  leadSource?: string;
  rmEmail?: string;
  designation?: string;
}

const AddLeadModal = ({
  open,
  onOpenChange,
  onAddLead,
  rmList,
  onLeadAdded,
  onUploadSuccess,
}: AddLeadModalProps) => {
  const [formData, setFormData] = useState({
    companyName: "",
    designation: "",
    name: "",
    mobile: "",
    email: "",
    leadType: "",
    leadSource: "",
    leadRM: "",
    leadStatusId: 1,
    location: "",
  });

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<CsvUploadResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successEntries, setSuccessEntries] = useState<DataType[]>([]);
  const [failedEntries, setFailedEntries] = useState<DataType[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const fieldLabelClass = "text-[14px] font-medium tracking-[-0.02em] text-slate-900";
  const fieldIconClass =
    "pointer-events-none absolute left-3 top-1/2 z-20 h-3.5 w-3.5 -translate-y-1/2 text-[#9aa7ba]";
  const fieldInputClass =
    "h-10 rounded-[14px] border-[#d9e2ee] bg-white pl-9 text-[14px] text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] placeholder:text-slate-400 focus:border-[#3f8b98] focus-visible:ring-[#3f8b98]/20";
  const fieldSelectClass =
    "h-10 rounded-[14px] border-[#d9e2ee] bg-white pl-9 pr-9 text-[14px] text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] focus:border-[#3f8b98] focus:ring-[#3f8b98]/20";

  const addLeadData = async (data: typeof formData) => {
    try {
      const response: any = await apiService.post("/leads/create", {
        companyName: data.companyName,
        leadRM: data.leadRM,
        name: data.name,
        phone: data.mobile,
        email: data.email,
        leadSource: data.leadSource,
        leadType: data.leadType,
        designation: data.designation,
        location: data.location,
      });
      if (String(response?.status) === "201") {
        toast.success("Lead added successfully");
        onOpenChange(false);
        onLeadAdded();
        return { success: true, message: "Lead added successfully" };
      }
      toast.error(response?.message || "Failed to add lead");
      return { success: false, message: response?.message || "Failed to add lead" };
    } catch (e: any) {
      console.error("Error adding lead:", e);
      const errorMessage = e.message || "Something went wrong. Please try again later.";
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};
    if (!formData.companyName.trim()) newErrors.companyName = "Company name is required";
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    if (!formData.leadRM) newErrors.leadRM = "Lead RM is required";
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    setFieldErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setFormError("Please fill Mobile Number, Company Name, Name, and Lead RM before creating the lead.");
      return false;
    }
    setFormError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await addLeadData(formData);
      if (result?.success) {
        setFormData({
          companyName: "",
          designation: "",
          name: "",
          mobile: "",
          email: "",
          leadRM: "",
          leadType: "",
          leadSource: "",
          leadStatusId: 1,
          location: "",
        });
        setFieldErrors({});
        setFormError(null);
      } else {
        setFormError(result?.message || "Failed to add lead. Please try again.");
      }
    } catch (error: any) {
      setFormError(error?.message || "Failed to add lead. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field as keyof typeof formData]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (formError) {
      setFormError(null);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      alert("No file selected");
      return;
    }
    setCsvFile(file);

    const uploadData = new FormData();
    uploadData.append("file", file as Blob);

    try {
      setIsProcessing(true);
      const response: any = await apiService.post("/leads/bulk-upload", uploadData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const successful = response.successEntries || [];
      const failed = response.failedEntries || [];
      setUploadResult({
        successful,
        failed,
        total: successful.length + failed.length,
      });
      if (successful.length > 0) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadFailedData = () => {
    if (!uploadResult?.failed.length) return;

    const csvContent = [
      ["Company Name", "Name", "Email", "Mobile", "Designation", "Lead Type", "Lead Source", "RM Email", "Error"],
      ...uploadResult.failed.map((row) => [
        row.companyName || "",
        row.name || "",
        row.email || "",
        row.phone || "",
        row.designation || "",
        row.leadType || "",
        row.leadSource || "",
        row.rmEmail || "",
        row.reason || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "failed_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSampleTemplate = () => {
    const sampleData = [
      ["companyName", "name", "designation", "phone", "email", "leadSource", "rmEmail", "location"],
      ["astrafin", "John Doe", "Manager", "4322333221", "john41@sample.com", "Website", "piyush@astrafin.org", "Bangalore"],
      ["astrafin ltd", "Jane Smith", "Director", "4322346789", "jane33@example.com", "Referral", "rahul@astrafin.org", "Mumbai"],
    ];

    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lead_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    const uploadData = new FormData();
    uploadData.append("file", file as Blob);

    try {
      const response: any = await apiService.post("/leads/bulk-upload", uploadData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setFileList([file as UploadFile]);
      setSuccessEntries(response.successEntries);
      setFailedEntries(response.failedEntries);
      const successful = response.successEntries || [];
      const failed = response.failedEntries || [];
      setUploadResult({
        successful,
        failed,
        total: successful.length + failed.length,
      });
      if (onSuccess) onSuccess(response.data, file);
    } catch (error: any) {
      console.error("Upload failed:", error);
      toast.error("Upload failed");
      if (onError) onError(error);
    }
  };

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isCSV = file.type === "text/csv";
    const isXLSX = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isCSV && !isXLSX) {
      toast.error("You can only upload CSV or XLSX files!");
    }

    setFileList([file]);
    handleUpload({ file } as unknown as UploadRequestOption);
    return false;
  };

  const onClickOfCancelData = () => {
    setFailedEntries([]);
    setSuccessEntries([]);
    setFileList([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isSubmitting) return;
        if (!nextOpen) {
          setFormError(null);
          setFieldErrors({});
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="w-[58vw] max-w-[880px] overflow-hidden rounded-[22px] border border-slate-200 bg-white p-0 shadow-[0_18px_48px_rgba(15,23,42,0.2)]">
        <Tabs defaultValue="single" className="flex max-h-[84vh] flex-col">
          <div className="border-b border-slate-200 px-5 pb-4 pt-4">
            <DialogTitle className="text-[20px] font-semibold tracking-[-0.04em] text-slate-900">
              Add New Lead
            </DialogTitle>

            <TabsList className="mt-3 grid h-[40px] w-full grid-cols-2 rounded-[16px] bg-[#eaedf2] p-1">
              <TabsTrigger
                value="single"
                className="rounded-[12px] text-[13px] font-semibold text-slate-400 shadow-none data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-[0_5px_14px_rgba(15,23,42,0.08)]"
              >
                Single Lead
              </TabsTrigger>
              <TabsTrigger
                value="bulk"
                className="rounded-[12px] text-[13px] font-semibold text-slate-400 shadow-none data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-[0_5px_14px_rgba(15,23,42,0.08)]"
              >
                Bulk Upload
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5">
            <TabsContent value="single" className="mt-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                {formError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {formError}
                  </div>
                )}

                <div className="grid gap-x-4 gap-y-3 lg:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="companyName" className={fieldLabelClass}>
                      Company Name <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className={fieldIconClass} />
                      <Input
                        id="companyName"
                        value={formData.companyName}
                        onChange={(e) => handleInputChange("companyName", e.target.value)}
                        placeholder="e.g. Acme Corp Ltd."
                        className={`${fieldInputClass} ${fieldErrors.companyName ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500/20" : ""}`}
                      />
                    </div>
                    {fieldErrors.companyName && (
                      <p className="text-[11px] text-red-500">{fieldErrors.companyName}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="designation" className={fieldLabelClass}>
                      Designation
                    </Label>
                    <div className="relative">
                      <BriefcaseBusiness className={fieldIconClass} />
                      <Input
                        id="designation"
                        value={formData.designation}
                        onChange={(e) => handleInputChange("designation", e.target.value)}
                        placeholder="e.g. CFO, Director"
                        className={fieldInputClass}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="name" className={fieldLabelClass}>
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <UserRound className={fieldIconClass} />
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="Full name"
                        className={`${fieldInputClass} ${fieldErrors.name ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500/20" : ""}`}
                      />
                    </div>
                    {fieldErrors.name && (
                      <p className="text-[11px] text-red-500">{fieldErrors.name}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="mobile" className={fieldLabelClass}>
                      Mobile Number <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Phone className={fieldIconClass} />
                      <Input
                        id="mobile"
                        value={formData.mobile}
                        onChange={(e) => handleInputChange("mobile", e.target.value)}
                        placeholder="+91 00000 00000"
                        className={`${fieldInputClass} ${fieldErrors.mobile ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500/20" : ""}`}
                      />
                    </div>
                    {fieldErrors.mobile && (
                      <p className="text-[11px] text-red-500">{fieldErrors.mobile}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className={fieldLabelClass}>
                      Email
                    </Label>
                    <div className="relative">
                      <Mail className={fieldIconClass} />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="email@example.com"
                        className={`${fieldInputClass} ${fieldErrors.email ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500/20" : ""}`}
                      />
                    </div>
                    {fieldErrors.email && (
                      <p className="text-[11px] text-red-500">{fieldErrors.email}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="leadRM" className={fieldLabelClass}>
                      Lead RM <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <UsersRound className={fieldIconClass} />
                      <Select value={formData.leadRM} onValueChange={(value) => handleInputChange("leadRM", value)}>
                        <SelectTrigger className={`${fieldSelectClass} ${fieldErrors.leadRM ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}>
                          <SelectValue placeholder="Select RM" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60 overflow-y-auto bg-white">
                          {rmList.map((rm) => (
                            <SelectItem key={rm.id} value={String(rm.id)}>
                              {rm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {fieldErrors.leadRM && (
                      <p className="text-[11px] text-red-500">{fieldErrors.leadRM}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="leadType" className={fieldLabelClass}>
                      Lead Type
                    </Label>
                    <div className="relative">
                      <Shapes className={fieldIconClass} />
                      <Select value={formData.leadType} onValueChange={(value) => handleInputChange("leadType", value)}>
                        <SelectTrigger className={fieldSelectClass}>
                          <SelectValue placeholder="Select lead type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="Hot">Hot</SelectItem>
                          <SelectItem value="Warm">Warm</SelectItem>
                          <SelectItem value="Cold">Cold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="leadSource" className={fieldLabelClass}>
                      Lead Source
                    </Label>
                    <div className="relative">
                      <Share2 className={fieldIconClass} />
                      <Select value={formData.leadSource} onValueChange={(value) => handleInputChange("leadSource", value)}>
                        <SelectTrigger className={fieldSelectClass}>
                          <SelectValue placeholder="Select lead source" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="Website">Website</SelectItem>
                          <SelectItem value="Social Media">Social Media</SelectItem>
                          <SelectItem value="Referral">Referral</SelectItem>
                          <SelectItem value="Cold Call">Cold Call</SelectItem>
                          <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    <Label htmlFor="location" className={fieldLabelClass}>
                      Location
                    </Label>
                    <div className="relative">
                      <MapPin className={fieldIconClass} />
                      <Input
                        id="location"
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleInputChange("location", e.target.value)}
                        placeholder="Enter location"
                        className={fieldInputClass}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                    className="h-9 rounded-[12px] border-slate-200 px-4 text-[13px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-9 rounded-[12px] bg-[#3f8b98] px-4 text-[13px] font-semibold text-white shadow-[0_8px_16px_rgba(63,139,152,0.2)] hover:bg-[#4a99a6] disabled:opacity-60"
                  >
                    <Plus className="mr-2 h-3 w-3" />
                    {isSubmitting ? "Adding Lead..." : "Add Lead"}
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="bulk" className="mt-0">
              {!uploadResult ? (
                <Card className="border-slate-200 shadow-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold text-slate-900">Bulk Upload</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-[18px] border-2 border-dashed border-slate-200 bg-slate-50/80 py-6 text-center">
                      <Upload className="mx-auto mb-2.5 h-8 w-8 text-slate-400" />
                      <p className="mb-2 text-gray-600">Drop your CSV file here or click to browse</p>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label htmlFor="csv-upload">
                        <Button variant="outline" className="border-slate-300 hover:bg-slate-50" asChild>
                          <span>Choose File</span>
                        </Button>
                      </label>
                      {csvFile && <p className="mt-2 text-sm text-slate-600">Selected: {csvFile.name}</p>}
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadSampleTemplate}
                        className="border-slate-300 hover:bg-slate-50"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download Sample Template
                      </Button>
                      <div className="text-sm text-gray-500">Supported format: CSV</div>
                    </div>

                    {csvFile && (
                      <div className="flex justify-end pt-4">
                        <Button disabled={isProcessing} className="bg-[#3f8b98] text-white hover:bg-[#4a99a6]">
                          {isProcessing ? "Processing..." : "Upload CSV"}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-slate-200 shadow-none">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base text-slate-900">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Upload Results
                    </CardTitle>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">✓ {uploadResult.successful.length} Successful</span>
                      <span className="text-red-600">✕ {uploadResult.failed.length} Failed</span>
                      <span className="text-gray-600">Total: {uploadResult.total}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="successful" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 bg-slate-100">
                        <TabsTrigger value="successful" className="data-[state=active]:bg-white data-[state=active]:text-slate-900">
                          Successful ({uploadResult.successful.length})
                        </TabsTrigger>
                        <TabsTrigger value="failed" className="data-[state=active]:bg-white data-[state=active]:text-slate-900">
                          Failed ({uploadResult.failed.length})
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="successful" className="space-y-4">
                        <div className="max-h-60 overflow-y-auto">
                          {uploadResult.successful.map((lead, index) => (
                            <div key={index} className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <div className="flex-1">
                                <p className="font-medium">{lead.companyName}</p>
                                <p className="text-sm text-gray-600">
                                  {lead.name} • {lead.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>

                      <TabsContent value="failed" className="space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">Failed records can be downloaded and corrected</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={downloadFailedData}
                            className="border-slate-300 hover:bg-slate-50"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download Failed Data
                          </Button>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {uploadResult.failed.map((lead, index) => (
                            <div key={index} className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                              <XCircle className="h-4 w-4 text-red-600" />
                              <div className="flex-1">
                                <p className="font-medium">{lead.companyName || "Unknown Company"}</p>
                                <p className="text-sm text-gray-600">
                                  {lead.name} • {lead.email}
                                </p>
                                <p className="text-sm text-red-600">{lead.reason}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    </Tabs>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setUploadResult(null);
                          setCsvFile(null);
                          onClickOfCancelData();
                        }}
                        className="h-9 rounded-[12px] border-slate-300 px-4 text-[13px]"
                      >
                        Upload Another File
                      </Button>
                      <Button
                        onClick={() => {
                          onOpenChange(false);
                          setUploadResult(null);
                          setCsvFile(null);
                          setIsProcessing(false);
                          setSuccessEntries([]);
                          setFailedEntries([]);
                          setFileList([]);
                        }}
                        className="h-9 rounded-[12px] bg-[#3f8b98] px-4 text-[13px] text-white hover:bg-[#4a99a6]"
                      >
                        Done
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadModal;
